import CommonTitleBar from "components/common/CommonTitleBar";
import Filters from "components/common/Filters";
import NoDataFound from "components/common/NoDataFound";
import Paginations from "components/common/Paginations";
import {
  formatDate,
  getMinMaxDate,
  getRelativeTimeFromNow,
  handleStopPropagation,
} from "components/common/Utils";
import { orderSpaceType } from "components/constants/DropdownOptions";
import {
  orderByOptions,
  orderStatusTypes,
} from "components/constants/StatusKeys";
import React, { useEffect, useState } from "react";
import { Col, Container, Dropdown, Row, Table } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { getBranchListWithCity } from "store/actions/branchSlice";
import { generateOrderInvoice, getAllOrders } from "store/actions/orderSlice";
import {
  resetFilterState,
  setSortedOrders,
  updateFilterState,
} from "store/reducers/orderReducer";
import Arrows from "assets/images/icons/arrows.svg";
import CustomStar from "components/common/CustomStar";
import { Ellipsis } from "lucide-react";
import { showAlert } from "store/reducers/alertReducer";
import TableShimmer from "components/ShimmerEffects/CommonShimmer/TableShimmer";
import Riyal from "assets/images/icons/riyal.svg";

const Orders = () => {
  const { t } = useTranslation();
  document.title = t("Bookings | Da Nonna");

  const navigate = useNavigate();
  const dispatch = useDispatch();

  const {
    loading,
    perPage,
    totalCount,
    totalPages,
    currentPage,
    orders,
    filterState,
  } = useSelector((state) => state.Orders);

  const [keyword, setKeyword] = useState(filterState?.keyword || "");
  const [dates, setDates] = useState(filterState?.dates || []);
  const [showDropdown, setShowDropdown] = useState(false);

  const [selectedFilters, setSelectedFilters] = useState(
    filterState?.selectedFilters || {
      status: -1,
      orderBy: -1,
      spaceTypeInt: -1,
      branches: [],
    }
  );

  useEffect(() => {
    dispatch(getBranchListWithCity());
    getAllData();
  }, []);

  const getAllData = (page, action) => {
    const { actionType } = action || {}; // SEARCH, RESET, RESET_KEYWORD
    const { orderBy, status, branches, spaceTypeInt } = selectedFilters;
    const pageToUse = page || filterState?.currentPage || 1;

    const isReset = actionType === "RESET";
    const payload = {
      perPage,
      pageNo: pageToUse,
      status: isReset ? -1 : status,
      orderBy: isReset ? -1 : orderBy,
      dateRange: isReset ? {} : getMinMaxDate(dates),
      branches: isReset ? [] : branches || [],
      keyword: ["RESET", "RESET_KEYWORD"].includes(actionType)
        ? ""
        : keyword || "",
      spaceTypeInt: isReset ? -1 : spaceTypeInt,
      branches: isReset ? [] : branches,
    };
    dispatch(getAllOrders(payload));

    // Update filter state in Redux when filters change
    dispatch(
      updateFilterState({
        currentPage: pageToUse,
        keyword,
        dates,
        selectedFilters,
      })
    );
  };

  const handleReset = () => {
    setKeyword("");
    setDates([]);
    setSelectedFilters({
      orderBy: -1,
      status: -1,
      spaceTypeInt: -1,
      branches: [],
    });
    // Reset filter state in Redux
    dispatch(resetFilterState());
    // getAllData(1, { actionType: "RESET" });
    const payload = {
      perPage,
      pageNo: 1,
      status: -1,
      orderBy: -1,
      dateRange: {},
      branches: [],
      keyword: "",
      spaceTypeInt: -1,
    };

    dispatch(getAllOrders(payload));
  };

  const filters = [
    {
      key: "orderBy",
      placeholder: "Order By",
      options: orderByOptions,
      type: "singleSelect",
    },
    {
      key: "spaceTypeInt",
      placeholder: "Space Type",
      options: orderSpaceType,
      type: "singleSelect",
    },
    {
      key: "status",
      placeholder: "Status",
      options: orderStatusTypes,
      type: "singleSelect",
    },
    {
      key: "branches",
      type: "branchCityFilter",
    },
  ];

  const [sortField, setSortField] = useState("");
  const [sortDirection, setSortDirection] = useState("asc");

  const SortableHeader = ({ field, label }) => (
    <div className="c_p" onClick={() => handleSort(field)}>
      <img src={Arrows} width={16} height={16} alt="arrow" />
      {t(label)}
    </div>
  );

  // Enhanced sorting function that can handle nested properties and different data types
  const handleSort = (field) => {
    const newDirection =
      sortField === field && sortDirection === "asc" ? "desc" : "asc";

    setSortField(field);
    setSortDirection(newDirection);

    const sortedOrders = [...orders].sort((a, b) => {
      // Function to get value from nested path
      const getNestedValue = (obj, path) => {
        const keys = path.split(".");
        let value = obj;

        for (const key of keys) {
          if (value === null || value === undefined) return null;
          value = value[key];
        }

        return value;
      };

      let valueA = getNestedValue(a, field);
      let valueB = getNestedValue(b, field);

      // Handle null/undefined values
      valueA = valueA === null || valueA === undefined ? "" : valueA;
      valueB = valueB === null || valueB === undefined ? "" : valueB;

      // Special handling for date fields
      if (field === "createdAt" || field === "bookingDate") {
        // Convert to timestamps for comparison
        const dateA = new Date(valueA).getTime();
        const dateB = new Date(valueB).getTime();

        return newDirection === "asc" ? dateA - dateB : dateB - dateA;
      }

      // For string values
      if (typeof valueA === "string" && typeof valueB === "string") {
        return newDirection === "asc"
          ? valueA.localeCompare(valueB)
          : valueB.localeCompare(valueA);
      }

      // For numbers and other types
      return newDirection === "asc"
        ? valueA < valueB
          ? -1
          : valueA > valueB
          ? 1
          : 0
        : valueB < valueA
        ? -1
        : valueB > valueA
        ? 1
        : 0;
    });

    dispatch(setSortedOrders(sortedOrders));
  };

  const handleGenerateInvoice = (bookingId) => {
    const payload = {
      bookingId,
    };
    dispatch(generateOrderInvoice(payload)).then((action) => {
      const { statusCode, success, message, data } = action?.payload;
      if (statusCode === 200 && success) {
        data && window.open(data, "_blank");
      } else {
        dispatch(showAlert({ text: message, type: "error" }));
      }
    });
  };

  return (
    <React.Fragment>
      <div className="page-content common_page">
        <Container fluid>
          <CommonTitleBar title={t("Bookings")} />
          <div className="common_wrapper">
            <Filters
              keyword={keyword}
              setKeyword={setKeyword}
              selectedFilters={selectedFilters}
              setSelectedFilters={setSelectedFilters}
              dates={dates}
              setDates={setDates}
              filters={filters}
              onSearch={getAllData}
              onReset={handleReset}
              className="filter_2" // white bg
            />
            <Row>
              <Col lg={12}>
                {loading ? (
                  <TableShimmer col={12} />
                ) : orders.length === 0 ? (
                  <NoDataFound />
                ) : (
                  <div
                    className="common_table_style"
                    style={{ minHeight: showDropdown ? 250 : "unset" }}
                  >
                    <Table>
                      <thead className="t_head">
                        <tr>
                          <th>
                            <SortableHeader
                              field="createdAt"
                              label="Booked On"
                            />
                          </th>
                          <th>{t("Status")}</th>
                          <th>{t("Booking No")}</th>
                          <th>{t("OS Version")}</th>
                          <th>{t("User")}</th>
                          <th>{t("Number")}</th>
                          <th>
                            <SortableHeader
                              field="bookingDate"
                              label="Booking Date"
                            />
                          </th>
                          <th>{t("Booking")}</th>
                          <th>
                            <div
                              className="c_p"
                              onClick={() => {
                                // Sort by the appropriate field based on space type
                                const qtyField =
                                  orders && orders[0]?.spaceTypeInt === 1
                                    ? "numberOfseats"
                                    : "durationInHour";
                                handleSort(qtyField);
                              }}
                            >
                              <img
                                src={Arrows}
                                width={16}
                                height={16}
                                alt="arrow"
                                style={{
                                  transform:
                                    sortField === "numberOfseats" ||
                                    sortField === "durationInHour"
                                      ? sortDirection === "asc"
                                        ? "rotate(0deg)"
                                        : "rotate(180deg)"
                                      : "none",
                                }}
                              />
                              {t("Qty")}
                            </div>
                          </th>
                          <th>
                            <SortableHeader
                              field="couponData.code"
                              label="Coupon"
                            />
                          </th>
                          <th>
                            <SortableHeader
                              field="splitPayments.total"
                              label="Total"
                            />
                          </th>
                          <th>{t("Rating")}</th>
                          <th>{t("Cancellation Reason")}</th>
                          <th>{t("Actions")}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {orders?.map((item, index) => (
                          <tr key={item?._id}>
                            <td>
                              <p className="w_fit_content">
                                {getRelativeTimeFromNow(
                                  item.createdAt,
                                  item?.timeZone
                                )}
                              </p>
                            </td>
                            <td>
                              <p
                                className={`w_fit_content ${
                                  orderStatusTypes?.find(
                                    (option) =>
                                      option.value === item.bookingStatus
                                  )?.class
                                }`}
                              >
                                {t(
                                  orderStatusTypes?.find(
                                    (option) =>
                                      option.value === item.bookingStatus
                                  )?.name
                                ) || "-"}
                              </p>
                            </td>
                            <td>
                              <p
                                className="c_p text-decoration-underline"
                                onClick={() =>
                                  navigate(
                                    `/bookings/bookings/booking-details?id=${item?._id}`
                                  )
                                }
                              >
                                #{item?.bookingNo || "-"}
                              </p>
                            </td>
                            <td>
                              {item?.platform === "android"
                                ? "Android"
                                : item?.platform}
                              {item?.appVersion && ` (${item?.appVersion})`}
                            </td>
                            <td>
                              <Link to={`/customers/customer-information?id=${item?.user?._id}`} className="text_primary text-decoration-underline">{item?.user?.name || "-"}</Link>
                            </td>
                            <td>{item?.user?.phone || "-"}</td>
                            <td>
                              {formatDate(item?.bookingDate, "dd MMM yyyy")}
                            </td>
                            <td>
                              <div style={{ minWidth: 200 }}>
                                <p>
                                  {
                                    orderSpaceType?.find(
                                      (option) =>
                                        option.value === item?.spaceTypeInt
                                    )?.name
                                  }{" "}
                                  - {item?.space?.name}
                                </p>
                                <p className="fs_12 text_gray">
                                  <span>{item?.city?.name}</span>
                                  {", "}
                                  <span>{item?.branch?.name}</span>
                                </p>
                              </div>
                            </td>
                            <td>
                              {item?.spaceTypeInt === 1
                                ? item?.numberOfseats
                                : [2, 3].includes(item?.spaceTypeInt)
                                ? `${item?.durationInHour}h`
                                : ""}
                            </td>
                            <td onClick={handleStopPropagation}>
                              {item?.couponData?.code ? (
                                <Link
                                  to={`/rewards/coupons/update-coupon?id=${item?.couponData?._id}`}
                                  className="text_primary fs_12 text-decoration-underline"
                                >
                                  {item?.couponData?.code}
                                </Link>
                              ) : (
                                "-"
                              )}
                            </td>
                            <td>
                              <img
                                src={Riyal}
                                width={15}
                                height={15}
                                alt="riyal"
                                className="me-1"
                              />
                              {item?.splitPayments?.total}
                            </td>
                            <td>
                              {item?.isRated ? (
                                <div style={{ minWidth: 100, maxWidth: 150 }}>
                                  <div className="d-flex gap-1 align-items-center">
                                    <p className="text_gray fs_14">
                                      {item?.rating}
                                    </p>
                                    <CustomStar rating={item?.rating} />
                                  </div>
                                  <p className="in_2_line">
                                    {item?.ratingData?.feedback || "-"}
                                  </p>
                                </div>
                              ) : (
                                "-"
                              )}
                            </td>
                            <td>
                              <p className="in_2_line">
                                {item?.cancelInfo?.cancelledNote || "-"}
                              </p>
                            </td>
                            <td>
                              <div className="d-flex align-items-center gap-3">
                                <Dropdown
                                  className="custom_dropdown list_action_dropdown"
                                  drop={"down"}
                                  autoClose="outside"
                                  onClick={handleStopPropagation}
                                  onToggle={(isOpen) => setShowDropdown(isOpen)}
                                >
                                  <Dropdown.Toggle>
                                    <Ellipsis className="text_black" />
                                  </Dropdown.Toggle>

                                  <Dropdown.Menu
                                    align="end"
                                    // popperConfig={{
                                    //   strategy: "fixed",
                                    //   modifiers: [
                                    //     {
                                    //       name: "preventOverflow",
                                    //       options: {
                                    //         boundary: "viewport",
                                    //       },
                                    //     },
                                    //     {
                                    //       name: "offset",
                                    //       options: {
                                    //         offset: [0, 5],
                                    //       },
                                    //     },
                                    //   ],
                                    // }}
                                  >
                                    <Dropdown.Item
                                      href="#"
                                      onClick={() => {
                                        navigate(
                                          `/customers/customer-information?id=${item?.user?._id}`
                                        );
                                      }}
                                    >
                                      {t("View Customer")}
                                    </Dropdown.Item>
                                    <Dropdown.Item
                                      href="#"
                                      onClick={() => {
                                        navigate(
                                          `/bookings/bookings/booking-details?id=${item?._id}`
                                        );
                                      }}
                                    >
                                      {t("View Booking")}
                                    </Dropdown.Item>
                                    <Dropdown.Item
                                      href="#"
                                      className="text_primary text-decoration-underline"
                                      onClick={() =>
                                        handleGenerateInvoice(item?._id)
                                      }
                                    >
                                      {t("Download Invoice")}
                                    </Dropdown.Item>
                                  </Dropdown.Menu>
                                </Dropdown>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                )}
              </Col>
            </Row>
            {!loading && (
              <Paginations
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={(page) => {
                  // Update filter state with new page
                  dispatch(
                    updateFilterState({
                      ...filterState,
                      currentPage: page,
                    })
                  );
                  getAllData(page);
                }}
                totalCount={totalCount}
              />
            )}
          </div>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default Orders;
